import { useEffect, useRef, useState } from "react";
import {
  ButtonVariant,
  Dialog,
  Link,
  SkeletonLoader,
  Text,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import { ArrowRight, CircleSlash, Equal, ExternalLink } from "lucide-react";
import { CodeBlockParameterBindingChange } from "../utils/BlueprintEditorUtils";

interface Props {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentCodeBlockName: string;
  currentVersion: string;
  newCodeBlockName: string;
  newVersion: string;
  currentGithubUrl?: string | null;
  newGithubUrl?: string | null;
  bindingChanges: CodeBlockParameterBindingChange[];
}

const CodeBlockBox = ({
  name,
  version,
  githubUrl,
}: {
  name: string;
  version: string;
  githubUrl?: string | null;
}) => (
  <div className="relative flex flex-1 flex-col items-center gap-y-1 rounded-lg border border-gray-10 p-4">
    {githubUrl && (
      <Tooltip title="View source on GitHub">
        <Link href={githubUrl} target="_blank" className="absolute right-2 top-2">
          <ExternalLink size={16} className="text-gray-40" />
        </Link>
      </Tooltip>
    )}
    <div className="flex flex-col items-center">
      {name.split(".").map((segment, index) => (
        <Text key={index} variant="h6" className="text-center break-words">
          {segment}
        </Text>
      ))}
    </div>
    <Text variant="sm" className="text-center text-gray-40">
      {version}
    </Text>
  </div>
);

const ParameterBindingCell = ({
  value,
  stepReferenceFieldPath,
  isUnchanged,
}: {
  value: string | null;
  stepReferenceFieldPath?: string | null;
  isUnchanged?: boolean;
}) => {
  if (value === null) {
    return (
      <Tooltip title="Not a parameter of this code block version">
        <CircleSlash size={16} className="text-gray-40" />
      </Tooltip>
    );
  }
  if (isUnchanged) {
    return (
      <Tooltip title={`Unchanged: ${value}`}>
        <div className="flex items-center gap-x-1.5 text-gray-40">
          <Equal size={14} className="shrink-0" />
          <Text variant="sm" className="block truncate text-gray-40">
            Same as current
          </Text>
        </div>
      </Tooltip>
    );
  }
  if (stepReferenceFieldPath) {
    return (
      <Tooltip title={stepReferenceFieldPath}>
        <Text className="block truncate">{value}</Text>
      </Tooltip>
    );
  }
  return <Text className="break-words">{value}</Text>;
};

const CodeBlockVersionSwapConfirmDialog = ({
  open,
  isLoading,
  onClose,
  onConfirm,
  currentCodeBlockName,
  currentVersion,
  newCodeBlockName,
  newVersion,
  currentGithubUrl,
  newGithubUrl,
  bindingChanges,
}: Props) => {
  // state
  const [isScrollable, setIsScrollable] = useState(false);

  // hooks
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // effects
  // Detect actual overflow (not just row count) so the bottom fade only shows when there's
  // really more content to scroll to.
  useEffect(() => {
    const el = scrollContainerRef.current;
    setIsScrollable(!!el && el.scrollHeight > el.clientHeight);
  }, [bindingChanges]);

  // This library's Tailwind config sets `important: true`, so the Dialog's built-in
  // `variant="lg"` width class (`w-[700px]`) compiles to `width: 700px !important` and blocks
  // the native CSS `resize` handle from growing the dialog horizontally (any replacement
  // Tailwind width class we add would carry the same `!important` and hit the same wall).
  // Removing the class via classList (rather than another className) leaves nothing to fight.
  useEffect(() => {
    if (!open) return;
    const bodyDiv = contentRef.current?.parentElement;
    const cardEl = bodyDiv?.parentElement;
    if (!cardEl) return;
    cardEl.classList.remove("w-[700px]");
    if (!cardEl.style.width) {
      cardEl.style.width = "1000px";
    }
    if (!cardEl.style.height) {
      cardEl.style.height = "700px";
    }
  }, [open]);

  return (
    <Dialog
      title="Swap code block"
      open={open}
      onClose={onClose}
      variant="lg"
      className="min-h-[500px] min-w-[500px] max-w-[95vw] max-h-[90vh] resize overflow-auto flex flex-col [&>div:nth-child(3)]:flex-1 [&>div:nth-child(3)]:overflow-hidden"
      compactFooterButtons
      primaryButtonText="Swap version"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      primaryButtonDisabled={isLoading}
      onPrimaryButtonClick={onConfirm}
      secondaryButtonText="Cancel"
      secondaryButtonVariant={ButtonVariant.TextBlack}
      onSecondaryButtonClick={onClose}
    >
      <div ref={contentRef} className="flex h-full flex-col">
        {isLoading ? (
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center gap-x-3">
              <SkeletonLoader height={68} borderRadius={8} fullWidth />
              <ArrowRight size={20} className="shrink-0 text-gray-40" />
              <SkeletonLoader height={68} borderRadius={8} fullWidth />
            </div>
            <div className="flex flex-col gap-y-2">
              <SkeletonLoader height={36} borderRadius={8} fullWidth />
              <SkeletonLoader height={36} borderRadius={8} fullWidth />
              <SkeletonLoader height={36} borderRadius={8} fullWidth />
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-col gap-y-4">
            <div className="flex items-center gap-x-3">
              <CodeBlockBox
                name={currentCodeBlockName}
                version={currentVersion}
                githubUrl={currentGithubUrl}
              />
              <ArrowRight size={20} className="shrink-0 text-gray-40" />
              <CodeBlockBox name={newCodeBlockName} version={newVersion} githubUrl={newGithubUrl} />
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                  <col className="w-1/3" />
                </colgroup>
                <thead className="border-y border-gray-10">
                  <tr>
                    <th className="px-6 py-3 text-left align-middle">
                      <Text variant="h6" className="whitespace-nowrap">
                        Parameter
                      </Text>
                    </th>
                    <th className="px-6 py-3 text-left align-middle">
                      <Text variant="h6" className="whitespace-nowrap">
                        Current
                      </Text>
                    </th>
                    <th className="px-6 py-3 text-left align-middle">
                      <Text variant="h6" className="whitespace-nowrap">
                        After
                      </Text>
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="relative min-h-0 flex-1">
                <div ref={scrollContainerRef} className="min-h-0 h-full overflow-y-auto pb-4">
                  <table className="w-full table-fixed text-sm">
                    <colgroup>
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                    </colgroup>
                    <tbody>
                      {bindingChanges.map((change) => (
                        <tr key={change.key} className="border-t border-gray-10">
                          <td className="overflow-hidden px-6 py-4 align-middle">
                            <Text className="block truncate">{change.key}</Text>
                          </td>
                          <td className="overflow-hidden px-6 py-4 align-middle">
                            <ParameterBindingCell
                              value={change.before}
                              stepReferenceFieldPath={change.stepReferenceFieldPath}
                            />
                          </td>
                          <td className="overflow-hidden px-6 py-4 align-middle">
                            <ParameterBindingCell
                              value={change.after}
                              stepReferenceFieldPath={change.stepReferenceFieldPath}
                              isUnchanged={change.before !== null && change.before === change.after}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {isScrollable && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-white" />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default CodeBlockVersionSwapConfirmDialog;
